<template>
  <v-container fluid class="pa-8">
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="white" elevation="2">
          <v-card-text class="py-6">
            <div class="d-flex align-center mb-4">
              <v-icon size="40" color="primary" class="mr-3">mdi-chart-timeline-variant</v-icon>
              <div>
                <h1 class="text-h4 font-weight-bold mb-0">Monitor en Tiempo Real</h1>
                <p class="text-body-2 text--secondary mb-0">Última consulta: {{ new Date().toLocaleString('es-MX') }}</p>
              </div>
            </div>
            
            <v-divider class="my-4"></v-divider>
            
            <p class="text-subtitle-1 font-weight-medium mb-3">Tipo de Sensor</p>
            <v-row>
              <v-col 
                v-for="tipo in tiposSensores" 
                :key="tipo.id"
                cols="12" sm="6" md="3"
              >
                <v-card 
                  class="sensor-type-card-clean"
                  :class="{ 'selected': filtros.tipoSensor === tipo.id }"
                  @click="seleccionarTipoSensor(tipo.id)"
                  hover
                  outlined
                  :disabled="tipo.disabled"
                >
                  <v-card-text class="text-center pa-4">
                    <v-avatar :color="tipo.color" size="50" class="mb-3">
                      <v-icon dark size="28">{{ tipo.icon }}</v-icon>
                    </v-avatar>
                    <h4 class="text-subtitle-1 font-weight-bold mb-1">{{ tipo.nombre }}</h4>
                    <p class="text-caption text--secondary mb-0">{{ tipo.descripcion }}</p>
                    <v-icon v-if="filtros.tipoSensor === tipo.id" color="success" class="mt-2">
                      mdi-check-circle
                    </v-icon>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-expand-transition>
      <v-row v-if="filtros.tipoSensor">
        <v-col cols="3">
          <v-select
            v-model="filtros.area"
            label="Área"
            :items="areas"
            item-text="nombre"
            item-value="idArea"
            @change="actualizarInstancias"
            :disabled="!filtros.tipoSensor"
          />
        </v-col>
        <v-col cols="3">
          <v-autocomplete
            v-model="filtros.instancia"
            label="Instancia"
            :items="instanciasFiltradas"
            :disabled="!filtros.area"
            :item-text="marcaModelo"
            item-value="idMaquinaIns"
            @change="actualizarSensores"
          >
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title>
                  {{ data.item.marca.trim() + " - " + data.item.modelo.trim() }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ data.item.nombre.trim() }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="filtros.clave"
            label="Sensor / Token"
            :items="clavesDisponibles"
            :disabled="!filtros.instancia || loadingClaves"
            :loading="loadingClaves"
            @change="iniciarMonitoreo"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="filtros.ventanaTiempo"
            label="Ventana de Tiempo"
            :items="ventanasTiempo"
            item-text="text"
            item-value="value"
            @change="ajustarVentana"
          />
        </v-col>
      </v-row>
    </v-expand-transition>

    <v-expand-transition>
      <v-row v-if="datosValidos">
        <v-col cols="12">
          <v-btn
            :color="monitoreando ? 'error' : 'success'"
            @click="toggleMonitoreo"
            class="mr-2"
          >
            <v-icon left>{{ monitoreando ? 'mdi-pause' : 'mdi-play' }}</v-icon>
            {{ monitoreando ? 'Pausar' : 'Iniciar' }} Monitoreo
          </v-btn>
          <v-btn
            color="warning"
            @click="limpiarDatos"
            :disabled="!chartMontado"
          >
            <v-icon left>mdi-delete-sweep</v-icon>
            Limpiar Datos
          </v-btn>
          <v-chip class="ml-4" color="primary">
            <v-icon left small>mdi-clock-outline</v-icon>
            Última actualización: {{ ultimaActualizacion }}
          </v-chip>
          <v-chip class="ml-2" :color="conectado ? 'success' : 'error'">
            <v-icon left small>{{ conectado ? 'mdi-connection' : 'mdi-close-network' }}</v-icon>
            {{ conectado ? 'Conectado' : 'Desconectado' }}
          </v-chip>
        </v-col>
      </v-row>
    </v-expand-transition>

<v-expand-transition>
  <v-row v-if="datosValidos && estadisticas.muestras > 0" class="mt-4">
    <v-col cols="12">
      <v-card class="white" elevation="2">
        <v-card-text class="pa-4">
          <v-row align="center">
            <v-col cols="12" sm="6" md="2">
              <div class="d-flex align-center">
                <v-avatar color="blue lighten-4" size="40" class="mr-3">
                  <v-icon color="blue darken-2">mdi-speedometer</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text--secondary">Valor Actual</div>
                  <div class="text-h5 font-weight-bold">{{ estadisticas.valorActual.toFixed(2) }}s</div>
                </div>
              </div>
            </v-col>
            
            <v-col cols="12" sm="6" md="2">
              <div class="d-flex align-center">
                <v-avatar color="green lighten-4" size="40" class="mr-3">
                  <v-icon color="green darken-2">mdi-chart-line</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text--secondary">Promedio</div>
                  <div class="text-h5 font-weight-bold">{{ estadisticas.promedio.toFixed(2) }}s</div>
                </div>
              </div>
            </v-col>
            
            <v-col cols="12" sm="6" md="2">
              <div class="d-flex align-center">
                <v-avatar color="orange lighten-4" size="40" class="mr-3">
                  <v-icon color="orange darken-2">mdi-arrow-up-bold</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text--secondary">Máximo</div>
                  <div class="text-h5 font-weight-bold">{{ estadisticas.maximo.toFixed(2) }}s</div>
                </div>
              </div>
            </v-col>
            
            <v-col cols="12" sm="6" md="2">
              <div class="d-flex align-center">
                <v-avatar color="purple lighten-4" size="40" class="mr-3">
                  <v-icon color="purple darken-2">mdi-arrow-down-bold</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text--secondary">Mínimo</div>
                  <div class="text-h5 font-weight-bold">{{ estadisticas.minimo.toFixed(2) }}s</div>
                </div>
              </div>
            </v-col>
        
            <v-col cols="12" sm="6" md="4" v-if="tendencia">
              <div class="d-flex align-center">
                <v-avatar :color="tendencia.color + ' lighten-4'" size="40" class="mr-3">
                  <v-icon :color="tendencia.color + ' darken-2'">{{ tendencia.icono }}</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text--secondary">Tendencia</div>
                  <div class="text-h6 font-weight-bold text-capitalize">
                    {{ tendencia.tipo }}
                    <v-chip x-small :color="tendencia.color" dark class="ml-2">
                      {{ tendencia.valor }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</v-expand-transition>

    <v-expand-transition>
      <v-row v-if="datosValidos" class="mt-4">
        <v-col cols="12">
          <v-card>
            <v-card-title>
              Tiempo de Ciclo - Token: {{ filtros.clave }}
              <v-spacer></v-spacer>
              <v-chip small>{{ estadisticas.muestras }} muestras</v-chip>
            </v-card-title>
            <v-card-text>
              <div v-if="!chartMontado" class="text-center pa-8">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <p class="mt-4">Esperando datos...</p>
              </div>
              <apexchart
                v-else
                ref="chart"
                height="450"
                :options="chartOptions"
                :series="series"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-expand-transition>

    <v-row v-if="logDatos.length > 0" class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Últimos puntos recibidos
          </v-card-title>
          <v-card-text>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Hora</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, i) in logDatos.slice(-10)" :key="i">
                  <td>{{ p.hora }}</td>
                  <td>{{ p.valor }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

<v-row v-if="historialSesiones.length > 0" class="mt-4">
  <v-col cols="12">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon left color="primary">mdi-history</v-icon>
        Historial de Sesiones de Monitoreo
        <v-spacer></v-spacer>
        <v-btn
          small
          outlined
          color="error"
          @click="limpiarHistorial"
        >
          <v-icon small left>mdi-delete</v-icon>
          Limpiar Historial
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-timeline dense>
          <v-timeline-item
            v-for="(sesion, index) in historialSesiones"
            :key="index"
            :color="sesion.color"
            small
            fill-dot
          >
            <template v-slot:icon>
              <v-icon dark small>{{ sesion.icon }}</v-icon>
            </template>
            
            <v-card class="elevation-2">
              <v-card-title class="text-h6 pb-2">
                <v-chip small :color="sesion.color" dark class="mr-2">
                  {{ sesion.token }}
                </v-chip>
                <span class="text-subtitle-2">{{ sesion.maquina }}</span>
                <v-spacer></v-spacer>
                <v-chip small outlined>
                  <v-icon small left>mdi-clock-outline</v-icon>
                  {{ sesion.duracion }}
                </v-chip>
              </v-card-title>
              
              <v-card-text class="pb-2">
                <v-row dense>
                  <v-col cols="12" sm="6" md="3">
                    <div class="d-flex align-center">
                      <v-icon small color="grey" class="mr-2">mdi-calendar</v-icon>
                      <div>
                        <div class="text-caption text--secondary">Fecha</div>
                        <div class="text-body-2 font-weight-medium">{{ sesion.fecha }}</div>
                      </div>
                    </div>
                  </v-col>
                  
                  
                  <v-col cols="12" sm="6" md="3">
                    <div 
                      class="d-flex align-center valores-clickable"
                      @click="toggleValoresSesion(index)"
                      style="cursor: pointer;"
                    >
                      <v-icon small color="blue" class="mr-2">mdi-counter</v-icon>
                      <div>
                        <div class="text-caption text--secondary">
                          Muestras 
                          <v-icon x-small>{{ sesion.mostrarValores ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                        </div>
                        <div class="text-body-2 font-weight-medium primary--text">
                          {{ sesion.muestras }} valores
                        </div>
                      </div>
                    </div>
                  </v-col>
                  
                  <v-col cols="12" sm="6" md="3">
                    <div class="d-flex align-center">
                      <v-icon small color="green" class="mr-2">mdi-chart-line</v-icon>
                      <div>
                        <div class="text-caption text--secondary">Promedio</div>
                        <div class="text-body-2 font-weight-medium">{{ sesion.promedio }}s</div>
                      </div>
                    </div>
                  </v-col>
                  
                  <v-col cols="12" sm="6" md="3">
                    <div class="d-flex align-center">
                      <v-icon small color="orange" class="mr-2">mdi-gauge</v-icon>
                      <div>
                        <div class="text-caption text--secondary">Rango</div>
                        <div class="text-body-2 font-weight-medium">{{ sesion.minimo }}s - {{ sesion.maximo }}s</div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
                
                
                <v-expand-transition>
                  <div v-if="sesion.mostrarValores" class="mt-3">
                    <v-divider class="mb-3"></v-divider>
                    <div class="d-flex align-center mb-2">
                      <v-icon small color="primary" class="mr-2">mdi-table</v-icon>
                      <span class="text-subtitle-2 font-weight-bold">Valores Recibidos</span>
                      <v-spacer></v-spacer>
                      <v-chip x-small color="primary" outlined>
                        Total: {{ sesion.datosGrafica.length }} puntos
                      </v-chip>
                    </div>
                    
                    <v-data-table
                      :headers="[
                        { text: '#', value: 'index', width: '60px' },
                        { text: 'Hora', value: 'hora', width: '150px' },
                        { text: 'Valor (s)', value: 'valor', align: 'end' }
                      ]"
                      :items="formatearValoresSesion(sesion.datosGrafica)"
                      :items-per-page="10"
                      dense
                      class="elevation-1"
                      :footer-props="{
                        'items-per-page-options': [5, 10, 20, 50],
                        'items-per-page-text': 'Filas por página:'
                      }"
                    >
                      <template v-slot:item.index="{ item }">
                        <v-chip x-small color="grey lighten-2">{{ item.index }}</v-chip>
                      </template>
                      
                      <template v-slot:item.valor="{ item }">
                        <v-chip 
                          small 
                          :color="getColorValor(item.valorNum, sesion.promedio)"
                          dark
                        >
                          {{ item.valor }}
                        </v-chip>
                        <v-chip v-if="tendencia" :color="tendencia.color" dark>
                        <v-icon left small>{{ tendencia.icono }}</v-icon>
                        Tendencia {{ tendencia.tipo }}
                      </v-chip>
                      </template>
                    </v-data-table>
                  </div>
                </v-expand-transition>
                
                
                <v-expand-transition>
                  <div v-if="sesion.mostrarGrafica" class="mt-3">
                    <v-divider class="mb-3"></v-divider>
                    <apexchart
                      type="line"
                      height="120"
                      :options="getHistorialChartOptions()"
                      :series="[{ name: sesion.token, data: sesion.datosGrafica }]"
                    />
                  </div>
                </v-expand-transition>
              </v-card-text>
              
              <v-card-actions>
                <v-btn
                  x-small
                  text
                  :color="sesion.mostrarValores ? 'primary' : 'grey'"
                  @click="toggleValoresSesion(index)"
                >
                  <v-icon small left>{{ sesion.mostrarValores ? 'mdi-table-eye-off' : 'mdi-table-eye' }}</v-icon>
                  {{ sesion.mostrarValores ? 'Ocultar' : 'Ver' }} Valores
                </v-btn>
                
                <v-btn
                  x-small
                  text
                  :color="sesion.mostrarGrafica ? 'primary' : 'grey'"
                  @click="toggleGraficaSesion(index)"
                >
                  <v-icon small left>{{ sesion.mostrarGrafica ? 'mdi-chevron-up' : 'mdi-chart-line' }}</v-icon>
                  {{ sesion.mostrarGrafica ? 'Ocultar' : 'Ver' }} Gráfica
                </v-btn>
                
                <v-spacer></v-spacer>
                
                <v-btn
                  x-small
                  text
                  color="error"
                  @click="eliminarSesion(index)"
                >
                  <v-icon small left>mdi-delete</v-icon>
                  Eliminar
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>
    </v-card>
  </v-col>
</v-row>

<v-dialog
  v-model="dialogEliminarHistorial"
  max-width="500"
  persistent
>
  <v-card>
    <v-card-text class="pa-6">
      <h2 class="text-h5 font-weight-medium mb-3">
        ¿Está seguro que desea eliminar todo el historial?
      </h2>
      
      <p class="text-body-1 text--secondary mb-0">
        Esta acción no se puede revertir y será permanente.
      </p>
      
      <div class="mt-2 text-body-2 text--secondary">
        Se eliminarán <strong>{{ historialSesiones.length }} sesiones</strong> de monitoreo.
      </div>
    </v-card-text>
    
    <v-card-actions class="px-6 pb-4">
      <v-spacer></v-spacer>
      <v-btn
        text
        color="success"
        @click="dialogEliminarHistorial = false"
        class="text-uppercase"
      >
        Cancelar
      </v-btn>
      <v-btn
        text
        color="error"
        @click="confirmarLimpiarHistorial"
        class="text-uppercase"
      >
        Eliminar
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>
<v-dialog
  v-model="dialogEliminarSesion"
  max-width="500"
  persistent
>
  <v-card v-if="sesionAEliminar !== null">
    <v-card-text class="pa-6">
      <h2 class="text-h5 font-weight-medium mb-3">
        ¿Está seguro que desea eliminar esta sesión?
      </h2>
      
      <p class="text-body-1 text--secondary mb-4">
        Esta acción no se puede revertir y será permanente.
      </p>
      
      <v-sheet
        color="grey lighten-4"
        rounded
        class="pa-3"
      >
        <div class="d-flex align-center mb-2">
          <v-chip 
            small 
            :color="historialSesiones[sesionAEliminar].color" 
            dark 
            class="mr-2"
          >
            {{ historialSesiones[sesionAEliminar].token }}
          </v-chip>
          <span class="text-body-2">{{ historialSesiones[sesionAEliminar].maquina }}</span>
        </div>
        <div class="text-caption text--secondary">
          {{ historialSesiones[sesionAEliminar].fecha }} • 
          {{ historialSesiones[sesionAEliminar].muestras }} muestras
        </div>
      </v-sheet>
    </v-card-text>
    
    <v-card-actions class="px-6 pb-4">
      <v-spacer></v-spacer>
      <v-btn
        text
        color="success"
        @click="dialogEliminarSesion = false; sesionAEliminar = null"
        class="text-uppercase"
      >
        Cancelar
      </v-btn>
      <v-btn
        text
        color="error"
        @click="confirmarEliminarSesion"
        class="text-uppercase"
      >
        Eliminar
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>
</v-container>
</template>

<script>
import axios from "axios";
import moment from "moment";
import Vue from "vue";

export default {
  data() {
    return {
      tiposSensores: [
        {
          id: 'produccion',
          nombre: 'Sensores de Tiempo ciclo',
          icon: 'mdi-factory',
          color: 'blue darken-2'
        },
        {
          id: 'temperatura',
          nombre: 'Sensores de Temperatura',
          icon: 'mdi-thermometer',
          color: 'orange darken-2',
          disabled: true 
        },
        {
          id: 'presion',
          nombre: 'Sensores de flujo electricidad',
          icon: 'mdi-sine-wave',
          color: 'green darken-2',
          disabled: true
        },
        {
          id: 'vibracion',
          nombre: 'Sensores de Vibración',
          icon: 'mdi-waveform',
          color: 'purple darken-2',
          disabled: true
        }
      ],
      
      logDatos: [],
      filtros: {
        tipoSensor: null,
        area: null,
        instancia: null,
        clave: null,
        ventanaTiempo: 300,
      },
      historialSesiones: [],
      sesionActual: null,
      tendencia: null,
      dialogEliminarHistorial: false,
      dialogEliminarSesion: false,
      sesionAEliminar: null,
      areas: [],
      instancias: [],
      clavesDisponibles: [],
      loadingClaves: false,
      ventanasTiempo: [
        { text: "30 segundos", value: 30 },
        { text: "1 minuto", value: 60 },
        { text: "5 minutos", value: 300 },
        { text: "15 minutos", value: 900 },
        { text: "30 minutos", value: 1800 },
        { text: "1 hora", value: 3600 },
      ],
      estadisticas: {
        valorActual: 0,
        promedio: 0,
        maximo: 0,
        minimo: 0,
        muestras: 0,
      },
      series: [{ name: "", data: [] }],
      chartMontado: false,
      monitoreando: false,
      conectado: false,
      ultimaActualizacion: "---",
      interval: null,
      maxPuntos: 200,
      chartOptions: {
        chart: {
          id: "realtime-chart",
          type: "line",
          height: 450,
          animations: {
            enabled: true,
            easing: "linear",
            dynamicAnimation: {
              speed: 300,
            },
          },
          zoom: {
            enabled: true,
          },
          toolbar: {
            show: true,
          },
        },
        stroke: {
          curve: "smooth",
          width: 2,
        },
        markers: {
          size: 0,
        },
        xaxis: {
          type: "datetime",
          labels: {
            datetimeUTC: false,
            format: "HH:mm:ss",
          },
        },
        yaxis: {
          title: {
            text: 'Tiempo de Ciclo (segundos)'
          },
          labels: {
            formatter: (val) => val?.toFixed(2) + 's' || "0.00s",
          },
        },
        tooltip: {
          x: {
            format: "dd/MM/yyyy HH:mm:ss",
          },
        },
        legend: {
          show: true,
          position: "top",
        },
      },
    };
  },
  computed: {
    datosValidos() {
      return this.filtros.tipoSensor && this.filtros.area && this.filtros.instancia && this.filtros.clave;
    },
    instanciasFiltradas() {
      if (!this.filtros.area) return [];
      return this.instancias.filter(i => i.idArea === this.filtros.area);
    },
  },
  mounted() {
    this.getAreas();
    this.getInstancias();
    this.cargarHistorialLocal();
  },
  beforeDestroy() {
    this.detenerMonitoreo();
  },

 methods: {
    marcaModelo: (item) => item.marca + " - " + item.modelo,
    
    seleccionarTipoSensor(tipo) {
      const tipoObj = this.tiposSensores.find(t => t.id === tipo);
      if (tipoObj && tipoObj.disabled) {
        return;
      }
      
      this.filtros.tipoSensor = tipo;
      this.filtros.area = null;
      this.filtros.instancia = null;
      this.filtros.clave = null;
      this.clavesDisponibles = [];
      this.detenerMonitoreo();
      this.limpiarDatos();
    },
    
    getAreas() {
      axios.get("/Areas")
        .then(resp => { this.areas = resp.data; })
        .catch(err => console.error("Error al obtener áreas:", err));
    },
    
    getInstancias() {
      axios.get("/MaquinaInstancias")
        .then(resp => { this.instancias = resp.data; })
        .catch(err => console.error("Error al obtener instancias:", err));
    },
    
    actualizarInstancias() {
      this.filtros.instancia = null;
      this.filtros.clave = null;
      this.clavesDisponibles = [];
      this.detenerMonitoreo();
      this.limpiarDatos();
    },
    
    async actualizarSensores() {
      this.filtros.clave = null;
      this.clavesDisponibles = [];
      this.detenerMonitoreo();
      this.limpiarDatos();
      
      if (!this.filtros.instancia) return;
      
      this.loadingClaves = true;
      try {
        const resp = await axios.get("/SensorDatos/TokensPorMaquina", {
          params: {
            idMaquinaIns: this.filtros.instancia
          }
        });
        
        this.clavesDisponibles = resp.data;
      } catch (err) {
        console.error("Error al obtener tokens:", err);
      } finally {
        this.loadingClaves = false;
      }
    },

  toggleValoresSesion(index) {
    this.$set(this.historialSesiones[index], 'mostrarValores', !this.historialSesiones[index].mostrarValores);
  },
  
  formatearValoresSesion(datosGrafica) {
    return datosGrafica.map((punto, idx) => ({
      index: idx + 1,
      hora: moment(punto.x).format('HH:mm:ss'),
      valor: punto.y.toFixed(2) + 's',
      valorNum: punto.y
    }));
  },
  
  getColorValor(valor, promedioStr) {
    const promedio = parseFloat(promedioStr);
    const diff = Math.abs(valor - promedio);
    const porcentaje = (diff / promedio) * 100;
    
    if (porcentaje < 5) return 'success'; //
    if (porcentaje < 15) return 'warning'; 
    return 'error'; 
  },
    
    iniciarMonitoreo() {
      if (!this.datosValidos) return;
      this.detenerMonitoreo();
      this.limpiarDatos();
      this.monitoreando = true;
      this.conectado = true;
      this.iniciarSesion();
      this.obtenerDatosIniciales();
      this.iniciarPolling();
    },
    

    detenerMonitoreo() {
      if (this.monitoreando && this.sesionActual) {
        this.finalizarSesion();
      }
      this.monitoreando = false;
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    
    toggleMonitoreo() {
      if (this.monitoreando) {
        this.detenerMonitoreo();
      } else {
        this.iniciarMonitoreo();
      }
    },
    
    limpiarDatos() {
      if (this.series[0]) {
        Vue.set(this.series[0], 'data', []);
        Vue.set(this.series[0], 'name', '');
      }
      this.logDatos = [];
      this.estadisticas = {
        valorActual: 0,
        promedio: 0,
        maximo: 0,
        minimo: 0,
        muestras: 0,
      };
      this.chartMontado = false;
      this.ultimaActualizacion = "---";
    },
    
    ajustarVentana() {
      if (this.monitoreando) {
        this.detenerMonitoreo();
        this.limpiarDatos();
        this.iniciarMonitoreo();
      }
    },
    
    iniciarPolling() {
      this.interval = setInterval(() => {
        if (this.monitoreando) {
          this.obtenerNuevosDatos();
        }
      }, 1000);
    },
    
    async obtenerDatosIniciales() {
      try {
        const fin = moment().utc();
        const inicio = moment().utc().subtract(this.filtros.ventanaTiempo, 'seconds');

        const resp = await axios.get("/SensorDatos/SeriesRealtime", {
          params: {
            inicio: inicio.format("YYYY-MM-DD HH:mm:ss"),
            fin: fin.format("YYYY-MM-DD HH:mm:ss"),
            idMaquinaIns: this.filtros.instancia,
            key: this.filtros.clave,
          },
        });

        const payload = (resp.data.series || []).map(serie => ({
          ...serie,
          data: serie.data.map(p => ({
            x: Number(p.x),
            y: Number(p.y)
          }))
        }));

        if (payload.length > 0 && payload[0].data.length > 0) {
          Vue.set(this.series, 0, {
            name: payload[0].name,
            data: [...payload[0].data]
          });
          
          this.calcularEstadisticas();
          this.chartMontado = true;
          this.ultimaActualizacion = moment().format("HH:mm:ss");
          this.conectado = true;
        } else {
          this.chartMontado = true;
        }
      } catch (err) {
        console.error("Error al obtener datos iniciales:", err);
        this.conectado = false;
        this.chartMontado = true;
      }
    },

    async obtenerNuevosDatos() {
      try {
        const fin = moment().utc();
        let inicio;

        if (this.series[0].data.length > 0) {
          const ultimoX = this.series[0].data[this.series[0].data.length - 1].x;
          inicio = moment(ultimoX).utc().subtract(1, "seconds");
        } else {
          inicio = moment().utc().subtract(5, "seconds");
        }

        const resp = await axios.get("/SensorDatos/SeriesRealtime", {
          params: {
            inicio: inicio.format("YYYY-MM-DD HH:mm:ss"),
            fin: fin.format("YYYY-MM-DD HH:mm:ss"),
            idMaquinaIns: this.filtros.instancia,
            key: this.filtros.clave,
          },
        });

        const payload = (resp.data.series || []).map(serie => ({
          ...serie,
          data: serie.data.map(p => ({
            x: Number(p.x),
            y: Number(p.y)
          }))
        }));

        if (payload.length > 0 && payload[0].data.length > 0) {
          this.conectado = true;
          this.agregarNuevosPuntos(payload[0]);
          this.ultimaActualizacion = moment().format("HH:mm:ss");
        }
      } catch (err) {
        console.error("Error al obtener nuevos datos:", err);
        this.conectado = false;
      }
    },

    calcularEstadisticas() {
      if (!this.series[0].data.length) return;
      
      const datos = this.series[0].data.map(d => d.y).filter(y => y !== null && y !== undefined);
      
      if (datos.length === 0) return;
      
      this.estadisticas.valorActual = datos[datos.length - 1];
      this.estadisticas.muestras = datos.length;
      this.estadisticas.promedio = datos.reduce((a, b) => a + b, 0) / datos.length;
      this.estadisticas.maximo = Math.max(...datos);
      this.estadisticas.minimo = Math.min(...datos);
      this.tendencia = this.calcularTendencia();
    },

    agregarNuevosPuntos(nuevaSerie) {
      let puntosAgregados = 0;
      
      nuevaSerie.data.forEach(punto => {
        const normalizado = {
          x: Number(punto.x),
          y: Number(punto.y)
        };

        const existe = this.series[0].data.some(p => p.x === normalizado.x);
        
        if (!existe) {
          this.series[0].data.push(normalizado);
          puntosAgregados++;

          this.logDatos.push({
            hora: moment(normalizado.x).format("HH:mm:ss"),
            valor: normalizado.y.toFixed(2)
          });

          if (this.logDatos.length > 50) {
            this.logDatos.shift();
          }
        }
      });

      if (puntosAgregados > 0) {
        this.series[0].data.sort((a, b) => a.x - b.x);

        const ultimoTimestamp = this.series[0].data.length > 0 
          ? this.series[0].data[this.series[0].data.length - 1].x 
          : Date.now();
        
        const tiempoLimite = ultimoTimestamp - (this.filtros.ventanaTiempo * 1000);
        const datosFiltrados = this.series[0].data.filter(p => p.x >= tiempoLimite);
        
        const datosFinales = datosFiltrados.length > this.maxPuntos 
          ? datosFiltrados.slice(-this.maxPuntos)
          : datosFiltrados;

        Vue.set(this.series[0], 'data', datosFinales);

        this.calcularEstadisticas();

        this.$nextTick(() => {
          if (this.$refs.chart && this.chartMontado) {
            this.$refs.chart.updateSeries([{
              name: this.series[0].name,
              data: this.series[0].data
            }], false);
          }
        });
      }
    },
    
  iniciarSesion() {
    const maquina = this.instancias.find(i => i.idMaquinaIns === this.filtros.instancia);
    this.sesionActual = {
      token: this.filtros.clave,
      maquina: maquina ? `${maquina.marca} - ${maquina.modelo}` : 'Desconocida',
      area: this.areas.find(a => a.idArea === this.filtros.area)?.nombre || 'N/A',
      inicio: moment(),
      datosGrafica: [],
      color: 'primary',
      icon: 'mdi-chart-line',
      mostrarGrafica: false,
      mostrarValores: false 
    };
  },
    
    finalizarSesion() {
      if (!this.sesionActual || this.estadisticas.muestras === 0) return;
      
      const duracionMs = moment().diff(this.sesionActual.inicio);
      const duracion = moment.duration(duracionMs);
      
      const sesionCompleta = {
        ...this.sesionActual,
        fecha: this.sesionActual.inicio.format('DD/MM/YYYY HH:mm'),
        duracion: `${Math.floor(duracion.asMinutes())}m ${duracion.seconds()}s`,
        fin: moment(),
        muestras: this.estadisticas.muestras,
        promedio: this.estadisticas.promedio.toFixed(2),
        maximo: this.estadisticas.maximo.toFixed(2),
        minimo: this.estadisticas.minimo.toFixed(2),
        datosGrafica: this.series[0].data.map(p => ({ x: p.x, y: p.y })),
        color: this.obtenerColorPorPromedio(this.estadisticas.promedio),
        mostrarGrafica: false
      };
      
      this.historialSesiones.unshift(sesionCompleta);
      this.guardarHistorialLocal();
      
      if (this.historialSesiones.length > 10) {
        this.historialSesiones = this.historialSesiones.slice(0, 10);
      }
      
      this.sesionActual = null;
    },
    
    obtenerColorPorPromedio(promedio) {
      if (promedio < 30) return 'success';
      if (promedio < 60) return 'warning';
      return 'error';
    },
    
    toggleGraficaSesion(index) {
      this.historialSesiones[index].mostrarGrafica = !this.historialSesiones[index].mostrarGrafica;
    },
    
    eliminarSesion(index) {
      this.sesionAEliminar = index;
      this.dialogEliminarSesion = true;
    },
      confirmarEliminarSesion() {
    const sesion = this.historialSesiones[this.sesionAEliminar];
    this.historialSesiones.splice(this.sesionAEliminar, 1);
    this.guardarHistorialLocal();
    this.dialogEliminarSesion = false;
    this.sesionAEliminar = null;
    this.$toast.success(`Sesión "${sesion.token}" eliminada`);
  },

  limpiarHistorial() {
    this.dialogEliminarHistorial = true;
  },

  confirmarLimpiarHistorial() {
    this.historialSesiones = [];
    this.guardarHistorialLocal();
    this.dialogEliminarHistorial = false;
    this.$toast.success('Historial eliminado correctamente');
  },

  guardarHistorialLocal() {
      try {
        localStorage.setItem('historialSensores', JSON.stringify(this.historialSesiones));
      } catch (err) {
        console.error('Error al guardar historial:', err);
      }
    },
    
    cargarHistorialLocal() {
      try {
        const historial = localStorage.getItem('historialSensores');
        if (historial) {
          this.historialSesiones = JSON.parse(historial);
        }
      } catch (err) {
        console.error('Error al cargar historial:', err);
      }
    },
    calcularTendencia() {
    if (this.series[0].data.length < 5) return null;
    
    const ultimos10 = this.series[0].data.slice(-10);
    const sumX = ultimos10.reduce((acc, _, i) => acc + i, 0);
    const sumY = ultimos10.reduce((acc, p) => acc + p.y, 0);
    const sumXY = ultimos10.reduce((acc, p, i) => acc + (i * p.y), 0);
    const sumX2 = ultimos10.reduce((acc, _, i) => acc + (i * i), 0);
    
    const n = ultimos10.length;
    const pendiente = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    
    return {
      tipo: pendiente > 0 ? 'ascendente' : 'descendente',
      valor: Math.abs(pendiente).toFixed(4),
      color: pendiente > 0 ? 'error' : 'success',
      icono: pendiente > 0 ? 'mdi-trending-up' : 'mdi-trending-down'
    };
  },
    
    getHistorialChartOptions() {
      return {
        chart: {
          type: 'line',
          sparkline: {
            enabled: true
          },
          animations: {
            enabled: false
          }
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        tooltip: {
          enabled: true,
          x: {
            format: 'HH:mm:ss'
          }
        },
        colors: ['#1976D2']
      };
    },
  }
};
</script>

<style scoped>
.sensor-type-card-clean {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 8px !important;
  border: 2px solid #e0e0e0 !important;
}

.sensor-type-card-clean:hover:not([disabled]) {
  border-color: #1976D2 !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
}

.sensor-type-card-clean.selected {
  border: 2px solid #4CAF50 !important;
  background-color: #f1f8f4 !important;
}

.sensor-type-card-clean[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}
.valores-clickable {
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 4px;
}

.valores-clickable:hover {
  background-color: rgba(25, 118, 210, 0.08);
  transform: scale(1.02);
}

.valores-clickable:active {
  transform: scale(0.98);
}

@media (max-width: 960px) {
  .text-h5 {
    font-size: 1.25rem !important;
  }
}

@media (max-width: 600px) {
  .text-h4 {
    font-size: 1.5rem !important;
  }
  
  .text-h5 {
    font-size: 1.1rem !important;
  }
}
</style>